import { Head, Link, usePage } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader, AdminSection, EmptyState } from "@/components/admin/AdminPageHeader"
import { PipelineBadge, StatusBadge } from "@/components/admin/Badges"
import { enquiryTitle, plural } from "@/components/admin/format"
import { MockupCard } from "@/components/admin/MockupCard"
import { Badge } from "@/components/ui/badge"
import { formatDay } from "@/lib/dates"
import type { AdminDashboardProps, AdminEnquirySummary, AdminLead, PageProps } from "@/types/inertia"

const TITLE = "Dashboard"
const DESCRIPTION =
  "What came in lately: the newest enquiries and orders, the latest brochure and mock-up leads, and the newest label designs."

export default function AdminDashboard() {
  const { props } = usePage<PageProps<AdminDashboardProps>>()
  const { counts, enquiries, leads, mockups } = props

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <AdminPageHeader title="Dashboard" description="What came in lately, at a glance." />

        <ul aria-label="At a glance" className="mt-6 grid grid-cols-3 gap-2 sm:gap-3">
          <StatTile label="Open enquiries" value={counts.open_enquiries} href="/admin/enquiries?pipeline=open" />
          <StatTile label="Wines carried" value={counts.wines_carried} href="/admin/wines?carried=yes" />
          <StatTile label="Leads this month" value={counts.leads_this_month} href="/admin/leads" />
        </ul>

        <AdminSection
          id="dashboard-enquiries"
          title="Newest enquiries"
          action={<SeeAll href="/admin/enquiries" label="enquiries" />}
        >
          {enquiries.length === 0 ? (
            <EmptyState>No enquiries yet. Orders and consultation requests from the site land here.</EmptyState>
          ) : (
            <ul
              aria-labelledby="dashboard-enquiries"
              className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page"
            >
              {enquiries.map((enquiry) => (
                <li key={enquiry.id}>
                  <EnquiryRow enquiry={enquiry} />
                </li>
              ))}
            </ul>
          )}
        </AdminSection>

        <AdminSection id="dashboard-leads" title="Latest leads" action={<SeeAll href="/admin/leads" label="leads" />}>
          {leads.length === 0 ? (
            <EmptyState>No leads yet. Everyone who downloads a mock-up or the brochure is listed here.</EmptyState>
          ) : (
            <ul
              aria-labelledby="dashboard-leads"
              className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page"
            >
              {leads.map((lead) => (
                <li key={lead.id}>
                  <LeadRow lead={lead} />
                </li>
              ))}
            </ul>
          )}
        </AdminSection>

        <AdminSection id="dashboard-mockups" title="Newest mock-ups">
          {mockups.length === 0 ? (
            <EmptyState>No label designs yet. Designs saved in the studio appear here.</EmptyState>
          ) : (
            <ul aria-labelledby="dashboard-mockups" className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {mockups.map((mockup) => (
                <li key={mockup.id}>
                  <MockupCard mockup={mockup} />
                </li>
              ))}
            </ul>
          )}
        </AdminSection>
      </AdminShell>
    </>
  )
}

/** A KPI tile: label and value in ink, the whole tile a link to the list behind it. */
function StatTile({ label, value, href }: { label: string; value: number; href: string }) {
  return (
    <li className="rounded-lg border border-hairline bg-surface">
      <Link
        href={href}
        className="flex h-full min-h-11 flex-col justify-between gap-1 rounded-lg p-3 no-underline hover:bg-hairline/20 sm:p-4"
      >
        <span className="text-xs text-ink-muted sm:text-sm">{label}</span>
        <span className="text-2xl font-semibold tabular-nums text-ink-display sm:text-3xl">{value.toLocaleString("en-GB")}</span>
      </Link>
    </li>
  )
}

function SeeAll({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      className="inline-flex min-h-11 items-center gap-1 text-sm text-ink-muted no-underline hover:text-ink-display"
    >
      See all<span className="sr-only"> {label}</span>
      <ChevronRight className="h-4 w-4" aria-hidden="true" />
    </Link>
  )
}

function EnquiryRow({ enquiry }: { enquiry: AdminEnquirySummary }) {
  return (
    <Link href={enquiry.url} className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-ink-display">
          {enquiryTitle(enquiry)} · {enquiry.name}
          {enquiry.company && <span className="font-normal text-ink-muted"> · {enquiry.company}</span>}
        </div>
        <div className="truncate text-xs text-ink-muted">
          {enquiry.event_type_label}
          {enquiry.bottle_count !== null && ` · ${plural(enquiry.bottle_count, "bottle")}`} · Received{" "}
          {formatDay(enquiry.created_at)}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <StatusBadge status={enquiry.status} label={enquiry.status_label} />
          <PipelineBadge pipeline={enquiry.pipeline} label={enquiry.pipeline_label} />
        </div>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
    </Link>
  )
}

function LeadRow({ lead }: { lead: AdminLead }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
      <div className="min-w-0 flex-1 basis-60">
        <div className="truncate text-sm font-medium text-ink-display">
          {lead.first_name ? `${lead.first_name} · ` : ""}
          <a href={`mailto:${lead.email}`}>{lead.email}</a>
        </div>
        <div className="truncate text-xs text-ink-muted">
          {formatDay(lead.created_at)} ·{" "}
          {lead.marketing_consent ? "Happy to hear from us" : "No marketing consent"}
        </div>
      </div>
      <Badge tone={lead.source === "brochure_download" ? "neutral" : "accent"}>{lead.source_label}</Badge>
    </div>
  )
}
